"use client";

import { useState } from "react";
import { useAccount } from "wagmi";
import { useTRPC } from "@/trpc/react";
import { useQuery } from "@tanstack/react-query";
import { GameWalletConnect } from "../wallet-connect";

const RARITY_COLORS: Record<string, string> = {
  Common: "#6a7a5a", Uncommon: "#4a9e3f", Rare: "#5b9ec4",
  Epic: "#9b59b6", Legendary: "#d4a853", Mythic: "#c0392b",
};

const ITEM_ICONS: Record<string, string> = {
  Weapon: "⚔️", Armor: "🛡️", Helmet: "⛑️", Shield: "🔰",
  Amulet: "📿", Ring: "💍", Cosmetic: "✨",
};

export default function InventoryPage() {
  const { address, isConnected } = useAccount();
  const trpc = useTRPC();
  const [tab, setTab] = useState<"gear" | "pets">("gear");

  const { data: loot, isLoading: lootLoading } = useQuery(
    trpc.loot.getByOwner.queryOptions(
      { walletAddress: address ?? "0x0000000000000000000000000000000000000000" },
      { enabled: isConnected && !!address }
    )
  );

  const { data: pets, isLoading: petsLoading } = useQuery(
    trpc.pet.getByOwner.queryOptions(
      { walletAddress: address ?? "0x0000000000000000000000000000000000000000" },
      { enabled: isConnected && !!address }
    )
  );

  if (!isConnected) {
    return (
      <div className="py-20 text-center">
        <h1 className="text-3xl font-black text-amber-400 uppercase tracking-wider mb-6">Inventory</h1>
        <p className="text-white/40 mb-8">Connect your wallet to view your gear and pets.</p>
        <GameWalletConnect />
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-7xl px-4 py-12">
      <h1 className="text-3xl font-black text-amber-400 uppercase tracking-wider mb-2">Inventory</h1>
      <p className="text-xs text-white/25 font-mono mb-6">{address}</p>

      <div className="flex gap-2 mb-8">
        {(["gear", "pets"] as const).map((t) => (
          <button
            key={t}
            onClick={() => setTab(t)}
            className={`px-5 py-2 rounded-lg text-sm font-bold uppercase tracking-wider transition-all ${
              tab === t
                ? "bg-green-500 text-black"
                : "bg-white/5 border-2 border-white/10 text-white/50 hover:border-green-500/30"
            }`}
          >
            {t === "gear" ? "⚔️ Gear" : "🐉 Pets"}
          </button>
        ))}
      </div>

      {tab === "gear" && (
        lootLoading ? (
          <div className="text-center py-20 text-white/25">Loading your loot...</div>
        ) : !loot || loot.length === 0 ? (
          <div className="text-center py-20">
            <p className="text-white/40 text-lg font-bold">No loot yet.</p>
            <p className="text-white/25 text-sm mt-2">Complete tower defense rounds to earn gear drops.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {loot.map((item) => (
              <div key={item.id} className="bg-white/[0.03] border border-white/[0.06] rounded-xl p-4 hover:border-green-500/30 transition-colors">
                <div className="flex items-center gap-3 mb-3">
                  <span className="text-2xl">{ITEM_ICONS[item.itemType] ?? "📦"}</span>
                  <div>
                    <h3 className="text-sm font-black text-amber-400 uppercase">{item.template?.name ?? `Item #${item.templateId}`}</h3>
                    <div className="flex gap-1 mt-0.5">
                      <span className="text-xs px-1.5 py-0.5 rounded bg-white/5 font-bold" style={{ color: RARITY_COLORS[item.rarity] }}>{item.rarity}</span>
                      <span className="text-xs px-1.5 py-0.5 rounded bg-white/5 text-white/40 font-bold">{item.itemType}</span>
                    </div>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-1 text-xs text-white/40">
                  <span>+{item.level} Enhanced</span>
                  <span>Stat1: {item.stat1}</span>
                  <span>Stat2: {item.stat2}</span>
                  <span>Stat3: {item.stat3}</span>
                </div>
                <p className="text-xs text-white/20 mt-2 font-mono">Token #{item.tokenId}</p>
              </div>
            ))}
          </div>
        )
      )}

      {tab === "pets" && (
        petsLoading ? (
          <div className="text-center py-20 text-white/25">Loading your companions...</div>
        ) : !pets || pets.length === 0 ? (
          <div className="text-center py-20">
            <p className="text-white/40 text-lg font-bold">No pets yet.</p>
            <p className="text-white/25 text-sm mt-2">Select a starter dragon to begin.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {pets.map((pet) => (
              <div key={pet.id} className="bg-white/[0.03] border border-white/[0.06] rounded-xl p-5 hover:border-green-500/30 transition-colors">
                <div className="w-16 h-16 mx-auto mb-3 rounded-full bg-white/5 flex items-center justify-center text-3xl border-2 border-white/10">🐉</div>
                <h3 className="text-center text-lg font-black text-green-400 uppercase">{pet.species?.name ?? `Pet #${pet.speciesId}`}</h3>
                <p className="text-center text-xs text-white/40 mb-3">{pet.element} | Lv. {pet.level}</p>
                <div className="grid grid-cols-2 gap-1 text-xs text-white/40">
                  <span>HP: {pet.hp}</span>
                  <span>ATK: {pet.attack}</span>
                  <span>DEF: {pet.defence}</span>
                  <span>SPD: {pet.speed}</span>
                </div>
              </div>
            ))}
          </div>
        )
      )}
    </div>
  );
}
